import {
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {createStyles, makeStyles, withStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import locale from '../locale/Localization';
import {getPersonsByCanton} from '../script/person-service.js';
import {NOTARY_FUNCTION_STATUS_ACTIVE} from '../utils/constants.js';
import DatePicker from './DatePicker';

const styles = (theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2)
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  });

const useStyles = makeStyles((theme) => ({
  textField: {
    margin: theme.spacing(1),
    padding: theme.spacing(0, 2, 2, 0)
  }
}));

const DialogTitle = withStyles(styles)((props) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant='h6'>{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label='close'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}))(MuiDialogActions);

/**
 * Customized dialog for NotarySuccessor.
 *
 * @param {*} props - Dialog attributes passed as property.
 * @return {string} - The component content.
 */
// eslint-disable-next-line max-lines-per-function
export default function NotarySuccessorDialog(props) {
  const [checked, setChecked] = useState(props.checked ?? false);
  const [options, setOptions] = useState([]);
  const classes = useStyles();

  const getSuccessorLabel = (notary) => {
    return notary
      ? notary.officialName +
          ',' +
          notary.firstNames +
          ' (' +
          notary.organisation.name +
          ')'
      : null;
  };

  useEffect(() => {
    getPersonsByCanton('VD').then((result) => {
      const notaries = [];
      result.forEach(function (notary) {
        if (notary.status === NOTARY_FUNCTION_STATUS_ACTIVE) {
          notaries.push({
            id: notary.id,
            organisation: {name: notary.organisation.name},
            officialName: notary.officialName,
            firstNames: notary.firstNames,
            label: getSuccessorLabel(notary)
          });
        }
      });
      setOptions(notaries);
    });
  }, []);

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={(_, reason) =>
          reason !== 'backdropClick' && props.handleClose()
        }
      >
        <DialogTitle
          id='successor-dialog-title'
          onClose={props.handleClose}
          style={{paddingBottom: 0}}
        >
          {locale.notarySuccessorDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={options}
            value={props.successor}
            onChange={props.handleSearchChange}
            getOptionLabel={(option) => getSuccessorLabel(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                disabled={false}
                helperText={locale.notarySuccessorTextHelper}
                margin='normal'
                label={locale.notarySuccessorTextLabel}
                fullWidth
                className={classes.textField}
              />
            )}
          />
          <DatePicker
            id='write-access-until'
            label={locale.successorWriteAccessUntil}
            handler={props.handleDateChange}
            value={props.accessDate}
            fullWidth={true}
            styles={classes.textField}
          />
          <FormControlLabel
            labelPlacement='start'
            style={{marginLeft: 10, marginRight: 0}}
            control={
              <Tooltip
                arrow
                title={
                  props.accessDate
                    ? locale.successorSignatureSwitchTooltip
                    : locale.successorSignatureSwitchTooltipHelper
                }
                placement='top'
                style={{whiteSpace: 'nowrap'}}
              >
                <span>
                  <Switch
                    checked={checked}
                    color='primary'
                    disabled={!props.accessDate}
                    onChange={() => setChecked(!checked)}
                  />
                </span>
              </Tooltip>
            }
            label={locale.successorSignatureSwitchLabel}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={props.handleClose} color='primary'>
            {locale.cancelButton}
          </Button>
          <Button
            autoFocus
            disabled={!props.successor}
            onClick={() => props.handleSubmit(checked)}
            color='primary'
            variant='contained'
          >
            {locale.confirmButton}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

NotarySuccessorDialog.propTypes = {
  open: PropTypes.bool,
  checked: PropTypes.bool,
  accessDate: PropTypes.string,
  successor: PropTypes.object,
  handleClose: PropTypes.func,
  handleChecked: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleSearchChange: PropTypes.func,
  handleDateChange: PropTypes.func
};
